import { useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from 'recharts';
import { ChartContainer } from './ChartContainer';
import {
  useCategorySpendingComparison,
  type CategorySpending,
} from '../../hooks/useCategorySpendingComparison';
import { formatMXN } from './constants';

const TOOLTIP_STYLE = {
  backgroundColor: 'white',
  border: '1px solid #e5e7eb',
  borderRadius: '0.375rem',
};

const TOOLTIP_WRAPPER_STYLE = {
  zIndex: 100,
};

interface CategorySpendingComparisonChartProps {
  height?: number;
  onCategoryClick?: (category: CategorySpending) => void;
}

interface BarClickData {
  payload?: CategorySpending;
}

export function CategorySpendingComparisonChart({
  height = 350,
  onCategoryClick,
}: CategorySpendingComparisonChartProps) {
  const { data } = useCategorySpendingComparison();

  const handleBarClick = useCallback(
    (clickData: BarClickData) => {
      if (onCategoryClick && clickData.payload) {
        onCategoryClick(clickData.payload);
      }
    },
    [onCategoryClick]
  );

  if (data.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-gray-500"
        style={{ height }}
      >
        No hay datos para el rango seleccionado
      </div>
    );
  }

  const maxNameLength = Math.max(...data.map((d) => d.categoryName.length));
  const yAxisWidth = Math.min(Math.max(maxNameLength * 8, 80), 160);

  return (
    <ChartContainer height={height}>
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
      >
        <XAxis
          type="number"
          tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
        />
        <YAxis
          type="category"
          dataKey="categoryName"
          width={yAxisWidth}
          tick={{ fontSize: 12 }}
        />
        <Tooltip
          formatter={(value) =>
            value !== undefined ? formatMXN(Number(value)) : ''
          }
          contentStyle={TOOLTIP_STYLE}
          wrapperStyle={TOOLTIP_WRAPPER_STYLE}
        />
        <Bar
          dataKey="amount"
          name="Gasto"
          maxBarSize={40}
          onClick={onCategoryClick ? handleBarClick : undefined}
          style={{ cursor: onCategoryClick ? 'pointer' : 'default' }}
        >
          {data.map((entry) => (
            <Cell key={entry.categoryId} fill={entry.color} />
          ))}
        </Bar>
      </BarChart>
    </ChartContainer>
  );
}
